import {
  EyeInvisibleOutlined,
  EyeOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { Skeleton } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AccountActions } from '@portal/actions';
import OverdraftInfoModal from '@portal/components/OverdraftInfoModal/OverdraftInfoModal';
import { PanelSideBarProps } from '@portal/components/PanelSidebar/PanelSidebar';
import { translate } from '@portal/services/i18n';
import { getEnumDescription } from '@portal/utils/enum';

import AccessListControl from '@portal/components/AccessListControl/AccessListControl';
import PaymentAccountSelector from '@portal/components/PaymentAccountSelector/PaymentAccountSelector';
import {
  myAccount,
  secondaryAccounts,
  enableOverdraft,
} from '@portal/config/app.json';
import RolesType from '@portal/enum/rolesType';
import useReduxState from '@portal/hooks/useReduxState';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import { setHeader } from '@portal/repositories/securitySdkConfig';
import { isCNPJ } from '@portal/services/masks';
import { StorageItems, getItem, setItem } from '@portal/services/storage';
import { removeLeadingZeros } from '@portal/utils/format';
import { hasAccess } from '@portal/utils/hasAccess';
import { getPaymentAccountByType } from '@portal/utils/paymentAccount';

const PanelSidebarProfile = ({
  balance,
  rawBalance,
  overdraft,
  rawOverdraft,
  name,
  bankCode,
  taxIdentifier,
  agency,
  investmentProfile,
}: PanelSideBarProps) => {
  const dispatch = useDispatch();

  const balanceError = useSelector(
    (state: reducers.rootReducer) => state.account.requestBalanceError
  );
  const {
    user: { me: loggedUser, roles },
    loading: { loadBalance },
    paymentAccount: { selectedPaymentAccount },
    theme: { currentTheme },
  } = useReduxState();

  const canReadBalance = hasAccess(roles, RolesType.BALANCE_READ);
  const [visible, setVisible] = useState<boolean>(
    JSON.parse(getItem('isBalanceVisible') ?? 'false')
  );
  const [investmentProfileFormatted, setInvestmentProfileFormatted] =
    useState('');
  const [overdraftModalVisible, setOverdraftModalVisible] = useState(false);

  const investmentAccount = getPaymentAccountByType(
    loggedUser,
    PaymentAccountType.INVESTMENTS
  );

  const handleShowBalance = () => {
    if (!balance) {
      dispatch(AccountActions.getUserBalance());
    }

    setVisible(true);
    setItem('isBalanceVisible', 'true');
  };

  const handleReloadBalance = () => {
    if (selectedPaymentAccount) {
      setHeader(
        StorageItems.PAYMENT_ACCOUNT_ID,
        selectedPaymentAccount.id,
        'session'
      );
    }
    dispatch(AccountActions.getUserBalance());
  };

  useEffect(() => {
    if (!investmentProfile) {
      return;
    }

    const investmentProfileType = getEnumDescription(
      investmentProfile,
      'INVESTMENT_PRODUCT_SUITABILITY_PROFILE'
    );

    const textInCapitalizeFormat =
      investmentProfileType.charAt(0).toUpperCase() +
      investmentProfileType.slice(1).toLowerCase();

    setInvestmentProfileFormatted(textInCapitalizeFormat);
  }, [investmentProfile]);

  return (
    <>
      <div className="panel-sidebar-profile">
        <div
          className="panel-sidebar-profile__user-info"
          style={{ borderTop: `2px solid ${currentTheme.borderColor}` }}
        >
          <div className="panel-sidebar-profile__personal-info">
            <h1 className="panel-sidebar-profile__name">{name}</h1>
            <div className="panel-sidebar-profile__account-info">
              <p>
                <span>
                  {translate(
                    `COMPONENTS.SIDEBAR_PROFILE.TAX_IDENTIFIER.${
                      isCNPJ(taxIdentifier) ? 'COMPANY' : 'PERSON'
                    }`
                  )}
                </span>
                : {taxIdentifier}
              </p>
              {bankCode ? (
                <p>
                  <span>
                    {translate('COMPONENTS.SIDEBAR_PROFILE.BANK_CODE')}
                  </span>
                  : {bankCode}
                </p>
              ) : null}
              {agency ? (
                <p className="panel-sidebar-profile__bank-details__span">
                  <span>
                    {translate('COMPONENTS.SIDEBAR_PROFILE.BANK_AGENCY')}
                  </span>
                  : {agency.padStart(4, '0')}
                </p>
              ) : null}
              {selectedPaymentAccount?.bankAccount ? (
                <p>
                  <span>
                    {translate('COMPONENTS.SIDEBAR_PROFILE.BANK_ACCOUNT')}
                  </span>
                  :{' '}
                  {myAccount.accountDigit ? (
                    <>
                      {' '}
                      {`${removeLeadingZeros(
                        selectedPaymentAccount?.bankAccount?.substring(
                          0,
                          selectedPaymentAccount?.bankAccount?.length - 1
                        )
                      )}-${selectedPaymentAccount?.bankAccount?.slice(-1)}`}
                    </>
                  ) : (
                    <>
                      {' '}
                      {removeLeadingZeros(
                        selectedPaymentAccount?.bankAccount?.replace(
                          /^(\d{5})(\d)$/,
                          '$1-$2'
                        )
                      )}
                    </>
                  )}
                </p>
              ) : null}
              {investmentProfile ? (
                <div className="panel-sidebar-profile__suitability-profile">
                  <p>
                    <span>
                      {translate(
                        'COMPONENTS.SIDEBAR_PROFILE.SUITABILITY_PROFILE'
                      )}
                    </span>
                    : {investmentProfileFormatted}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {secondaryAccounts.enable &&
      getPaymentAccountByType(
        loggedUser,
        PaymentAccountType.ACCOUNTSECONDARY
      ) ? (
        <PaymentAccountSelector sideMenuInput={true} />
      ) : null}
      {(selectedPaymentAccount || investmentAccount) && canReadBalance ? (
        <div
          className="panel-sidebar-profile__section"
          style={{ borderTop: `2px solid ${currentTheme.borderColor}` }}
        >
          <div className="panel-sidebar-profile__balance-container">
            <p className="panel-sidebar-profile__balance_text">
              {translate('COMPONENTS.SIDEBAR_PROFILE.BALANCE')}
            </p>
            {!loadBalance && (
              <div className="panel-sidebar-profile__icons">
                {visible ? (
                  <>
                    <EyeInvisibleOutlined
                      {...{ testId: 'home_EyeInvisibleOutlined_visible' }}
                      className="panel-sidebar-profile__eye-icon"
                      onClick={() => {
                        setVisible(false);
                        setItem('isBalanceVisible', 'false');
                      }}
                    />

                    <UndoOutlined
                      className="panel-sidebar-profile__undo-icon"
                      onClick={handleReloadBalance}
                    />
                  </>
                ) : null}
                {!visible && (
                  <EyeOutlined
                    {...{ testId: 'home_EyeOutlined_visible' }}
                    onClick={handleShowBalance}
                    className="panel-sidebar-profile__eye-icon"
                  />
                )}
              </div>
            )}
          </div>
          {!loadBalance || !!balance ? (
            <AccessListControl
              accessRole={RolesType.BALANCE_READ}
              NoAccessComponent={<Skeleton.Button style={{ width: 150 }} />}
            >
              <div>
                {visible && !loadBalance ? (
                  <div className="panel-sidebar-profile__balance-container">
                    <h1
                      {...{ testId: 'home_h1_balance' }}
                      className="panel-sidebar-profile__balance"
                    >
                      {balanceError || balance === null
                        ? translate(
                            'COMPONENTS.SIDEBAR_PROFILE.REQUEST_BALANCE_ERROR'
                          )
                        : balance}
                    </h1>
                  </div>
                ) : !visible && !loadBalance ? (
                  <div className="panel-sidebar-profile__balance-container">
                    <h1
                      {...{ testId: 'home_h1_balance_hidden' }}
                      className="panel-sidebar-profile__balance"
                    >
                      ●●●●●●
                    </h1>
                  </div>
                ) : (
                  <div className="panel-sidebar-profile__balance-container">
                    <Skeleton.Button
                      size="large"
                      active={!!loadBalance}
                      className="panel-sidebar-profile__skeleton"
                    />
                  </div>
                )}
                {enableOverdraft &&
                visible &&
                rawOverdraft !== null &&
                rawOverdraft !== undefined &&
                rawOverdraft > 0 &&
                !loadBalance &&
                typeof overdraft !== 'undefined' ? (
                  <div
                    className="panel-sidebar-profile__overdraft-container"
                    style={{ marginTop: 12 }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span
                          className="panel-sidebar-profile__overdraft-label"
                          style={{ fontWeight: 500, fontSize: 14 }}
                        >
                          {translate('COMPONENTS.SIDEBAR_PROFILE.OVERDRAFT')}
                        </span>
                      </div>
                      <span
                        className="panel-sidebar-profile__overdraft-value"
                        style={{ fontWeight: 700, fontSize: 16 }}
                      >
                        {overdraft}
                      </span>

                      {/* TODO: Liberar quando JD disponibilizar */}

                      {/* <InfoCircleOutlined
                          style={{
                            marginLeft: 8,
                            fontSize: 14,
                            cursor: 'pointer',
                          }}
                          onClick={() => setOverdraftModalVisible(true)}
                        /> */}
                    </div>

                    {rawBalance !== null &&
                      rawBalance !== undefined &&
                      rawBalance < 0 && (
                        <div>
                          <span
                            className="panel-sidebar-profile__overdraft-used"
                            style={{ fontWeight: 600, fontSize: 13 }}
                          >
                            {translate(
                              'COMPONENTS.SIDEBAR_PROFILE.OVERDRAFT_USED'
                            )}
                          </span>
                        </div>
                      )}
                  </div>
                ) : null}
              </div>
            </AccessListControl>
          ) : (
            <Skeleton.Button active style={{ width: 150 }} />
          )}
        </div>
      ) : null}
      <OverdraftInfoModal
        visible={overdraftModalVisible}
        onClose={() => setOverdraftModalVisible(false)}
      />
    </>
  );
};

export default PanelSidebarProfile;
